import React, { useState, useEffect } from 'react';
import { getFirebaseStorage } from '../../firebase.js';
import { ref, getDownloadURL, uploadBytes, getMetadata } from 'firebase/storage';
import './ControlPanel.css';

const FilesManagement = () => {
    const [files, setFiles] = useState({
        terms: { content: '', loading: false, lastModified: null },
        privacypolicy: { content: '', loading: false, lastModified: null },
        faq: { content: '', loading: false, lastModified: null },
        advertisementpopup: { content: '', loading: false, lastModified: null }
    });
    const [uploading, setUploading] = useState({});
    const [message, setMessage] = useState('');
    const [expandedFiles, setExpandedFiles] = useState({
        terms: true,
        privacypolicy: true,
        faq: true,
        advertisementpopup: true
    });

    const fileConfigs = {
        terms: {
            name: 'terms.json',
            title: 'Términos y Condiciones',
            path: 'resources/terms.json'
        },
        privacypolicy: {
            name: 'privacypolicy.json',
            title: 'Política de Privacidad',
            path: 'resources/privacypolicy.json'
        },
        faq: {
            name: 'faq.json',
            title: 'Preguntas Frecuentes',
            path: 'resources/faq.json'
        },
        featuredproviders: {
            name: 'featuredproviders.json',
            title: 'Proveedores del Banner',
            path: 'resources/featuredproviders.json'
        },
        advertisementpopup: {
            name: 'advertisementpopup.json',
            title: 'Popup de Publicidad',
            path: 'resources/advertisementpopup.json'
        }
    };

    useEffect(() => {
        loadAllFiles();
    }, []);

    const loadAllFiles = async () => {
        for (const fileKey of Object.keys(fileConfigs)) {
            await loadFile(fileKey);
        }
    };

    const loadFile = async (fileKey) => {
        setFiles(prev => ({
            ...prev,
            [fileKey]: { ...prev[fileKey], loading: true }
        }));

        try {
            const config = fileConfigs[fileKey];
            const storage = getFirebaseStorage(); // Obtener la instancia de storage
            const fileRef = ref(storage, config.path);

            // Obtener metadatos para la fecha de modificación
            const metadata = await getMetadata(fileRef);
            const lastModified = metadata.updated || metadata.timeCreated;

            // Obtener el contenido del archivo
            const url = await getDownloadURL(fileRef);
            const response = await fetch(url);
            const content = await response.text();

            // Formatear JSON para mejor lectura
            const parsedContent = JSON.parse(content);
            const formattedContent = JSON.stringify(parsedContent, null, 2);

            setFiles(prev => ({
                ...prev,
                [fileKey]: {
                    content: formattedContent,
                    loading: false,
                    lastModified: new Date(lastModified).toLocaleString()
                }
            }));
        } catch (error) {
            console.error(`Error loading ${fileKey}:`, error);
            setFiles(prev => ({
                ...prev,
                [fileKey]: {
                    content: `Error al cargar el archivo: ${error.message}`,
                    loading: false,
                    lastModified: null
                }
            }));
        }
    };

    const handleContentChange = (fileKey, newContent) => {
        setFiles(prev => ({
            ...prev,
            [fileKey]: { ...prev[fileKey], content: newContent }
        }));
    };

    const validateAndFormatJSON = (content) => {
        try {
            const parsed = JSON.parse(content);
            return JSON.stringify(parsed, null, 2);
        } catch (error) {
            throw new Error(`JSON inválido: ${error.message}`);
        }
    };

    const uploadFile = async (fileKey) => {
        setUploading(prev => ({ ...prev, [fileKey]: true }));
        setMessage('');

        try {
            const config = fileConfigs[fileKey];
            const content = files[fileKey].content;

            // Validar JSON antes de subir
            const formattedContent = validateAndFormatJSON(content);

            // Crear blob con el contenido
            const blob = new Blob([formattedContent], { type: 'application/json' });

            // Subir a Firebase Storage
            const storage = getFirebaseStorage(); // Obtener la instancia de storage
            const fileRef = ref(storage, config.path);
            await uploadBytes(fileRef, blob);

            setMessage(`✅ ${config.title} actualizado correctamente`);

            // Recargar el archivo para obtener la nueva fecha de modificación
            await loadFile(fileKey);

        } catch (error) {
            console.error(`Error uploading ${fileKey}:`, error);
            setMessage(`❌ Error al subir ${fileConfigs[fileKey].title}: ${error.message}`);
        } finally {
            setUploading(prev => ({ ...prev, [fileKey]: false }));

            // Limpiar mensaje después de 5 segundos
            setTimeout(() => setMessage(''), 5000);
        }
    };

    const reloadFile = async (fileKey) => {
        await loadFile(fileKey);
        setMessage(`🔄 ${fileConfigs[fileKey].title} recargado`);
        setTimeout(() => setMessage(''), 3000);
    };

    const toggleFileExpansion = (fileKey) => {
        setExpandedFiles(prev => ({
            ...prev,
            [fileKey]: !prev[fileKey]
        }));
    };

    return (
        <div className="control-panel-content files-management-container">
            <div className="control-panel-card wide-card">
                <div className="card-header">
                    <h3>📁 Gestión de Archivos JSON</h3>
                </div>
                <div className="card-content">
                    <p>
                        Gestiona los archivos JSON almacenados en Firebase Storage.
                        Puedes editar el contenido y subirlo directamente.
                    </p>
                    {message && (
                        <div className={`alert ${message.includes('❌') ? 'alert-danger' : 'alert-success'}`}>
                            {message}
                        </div>
                    )}
                </div>
            </div>

            {Object.entries(fileConfigs).map(([fileKey, config]) => (
                <div key={fileKey} className={`control-panel-card wide-card expandable-card ${expandedFiles[fileKey] ? 'expanded' : ''}`}>
                    <div className="card-header" onClick={() => toggleFileExpansion(fileKey)}>
                        <div className="file-header-content">
                            <h4>{config.title}</h4>
                            <div className="file-expand-indicator">
                                {expandedFiles[fileKey] ? '📂' : '📁'}
                            </div>
                        </div>
                        {expandedFiles[fileKey] && (
                            <div className="file-actions" onClick={(e) => e.stopPropagation()}>
                                <button
                                    className="provider-btn provider-btn-outline"
                                    onClick={() => reloadFile(fileKey)}
                                    disabled={files[fileKey].loading}
                                >
                                    🔄 Recargar
                                </button>
                                <button
                                    className="provider-btn provider-btn-primary"
                                    onClick={() => uploadFile(fileKey)}
                                    disabled={uploading[fileKey] || files[fileKey].loading}
                                >
                                    {uploading[fileKey] ? '⏳ Subiendo...' : '📤 Guardar'}
                                </button>
                            </div>
                        )}
                    </div>
                    {expandedFiles[fileKey] && (
                        <div className="card-content">
                            <div className="file-info">
                                <small>
                                    <strong>Archivo:</strong> {config.name} |
                                    <strong> Última modificación:</strong> {files[fileKey].lastModified || 'Cargando...'}
                                </small>
                            </div>

                            {files[fileKey].loading ? (
                                <div className="loading-indicator">
                                    <p>⏳ Cargando archivo...</p>
                                </div>
                            ) : (
                                <textarea
                                    className="json-editor"
                                    value={files[fileKey].content}
                                    onChange={(e) => handleContentChange(fileKey, e.target.value)}
                                    placeholder={`Contenido de ${config.title}...`}
                                    rows={20}
                                    spellCheck={false}
                                />
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default FilesManagement;
